<template>
    <div
        :class="is_icon
            ? 'd-inline-flex align-items-center justify-content-center lh-1'
            : 'preview-root d-flex flex-column w-100 h-100 overflow-hidden'"
        :style="is_icon ? null : previewRootStyle"
    >
        <!-- アイコンモード -->
        <template v-if="is_icon">
            <button
                type="button"
                class="btn p-0 border-0 bg-transparent lh-1 align-baseline"
                :class="is_pdf_file ? 'text-danger' : 'text-secondary'"
                :title="receipt_image?.file_name || (is_pdf_file ? 'PDF' : '画像')"
                @click="openLightbox"
            >
                <i :class="is_pdf_file ? 'fa-solid fa-file-pdf fs-6' : 'fa-solid fa-file-image fs-6'"></i>
                <span v-if="show_filename">
                    {{ receipt_image?.file_name }}
                </span>
            </button>
        </template>

        <!-- 通常モード -->
        <template v-else>
            <div class="preview-media flex-grow-1 min-h-0 overflow-hidden bg-white rounded position-relative">
                <template v-if="is_pdf_file">
                    <iframe
                        :src="pdfInlineSrc"
                        class="pdf-receipt w-100 h-100 border-0"
                        title="領収書PDFプレビュー"
                    ></iframe>

                    <div class="position-absolute top-0 end-0 p-2 d-flex gap-2">
                        <button type="button" class="btn btn-sm btn-light border" @click="openLightbox">
                            <i class="fa-solid fa-up-right-and-down-left-from-center me-1"></i>
                            拡大
                        </button>
                        <a :href="raw_file_url" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-primary">
                            <i class="fa-solid fa-arrow-up-right-from-square me-1"></i>
                            新規タブ
                        </a>
                    </div>
                </template>

                <template v-else>
                    <img :src="raw_file_url" class="img-receipt w-100 h-100 d-block" alt="領収書画像" @click="openLightbox" />
                    <div class="position-absolute top-0 end-0 p-2">
                        <button type="button" class="btn btn-sm btn-light border" @click="openLightbox">
                            <i class="fa-solid fa-up-right-and-down-left-from-center me-1"></i>
                            拡大
                        </button>
                    </div>
                </template>
            </div>

            <div class="small text-secondary p-2 bg-white rounded text-center mt-2 text-break">
                <i :class="is_pdf_file ? 'fa-solid fa-file-pdf me-2 text-danger' : 'fa-solid fa-file-image me-2 text-muted'"></i>
                <span>{{ receipt_image?.file_name }}</span>
            </div>
        </template>
    </div>
</template>

<script>
import GLightbox from 'glightbox';
export default {
    props: {
        receipt_image: { type: Object, required: true },
        receipt: {
            type: Object,
            default() {
                return {};
            }
        },
        is_icon: {
            type: Boolean,
            default: false
        },
        show_filename: {
            type: Boolean,
            default: true
        },
        use_pdfjs: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            lightbox: null,
            parent_height: null,
            resize_observer: null
        };
    },
    computed: {
        previewRootStyle() {
            return this.parent_height && this.parent_height > 0 ? { height: `${this.parent_height}px` } : { height: '100%' };
        },
        raw_file_url() {
            return `${this.receipt_image?.path || ''}`;
        },
        is_pdf_file() {
            const file_name = `${this.receipt_image?.file_name || ''}`.toLowerCase();
            const file_path = `${this.receipt_image?.path || ''}`.toLowerCase().split('?')[0];
            return file_name.endsWith('.pdf') || file_path.endsWith('.pdf');
        },
        pdfInlineSrc() {
            return `${this.raw_file_url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;
        }
    },
    mounted() {
        if (!this.is_icon) {
            this.bindParentHeight();
        }
        this.rebuildLightbox();
    },
    beforeUnmount() {
        this.destroyLightbox();
        this.unbindParentHeight();
    },
    watch: {
        'receipt_image.path'() {
            this.rebuildLightbox();
        },
        'receipt_image.file_name'() {
            this.rebuildLightbox();
        },
        is_icon(next_value) {
            if (next_value) {
                this.unbindParentHeight();
                this.parent_height = null;
            } else {
                this.bindParentHeight();
            }
        }
    },
    methods: {
        /**
         * PDF表示用のiframe HTMLを生成する。
         * URLは信頼済み（自システム発行）を前提とする。
         * @returns {string}
         */
        buildPdfIframeHtml() {
            const pdf_url = `${this.raw_file_url}`;
            return `<iframe src="${pdf_url}" style="width:100%;height:90vh;border:0;" allowfullscreen></iframe>`;
        },
        buildLightboxElements() {
            const base_title = `${this.receipt_image?.file_name || ''}`;
            const base_description = `${this.receipt?.client?.client_name || '—'}`;

            if (this.is_pdf_file) {
                return [
                    {
                        type: 'inline',
                        content: this.buildPdfIframeHtml(),
                        title: base_title,
                        description: base_description,
                        width: '80vw',
                        height: '80vh'
                    }
                ];
            }

            return [
                {
                    href: this.raw_file_url,
                    type: 'image',
                    title: base_title,
                    description: base_description
                }
            ];
        },

        rebuildLightbox() {
            this.$nextTick(() => {
                this.destroyLightbox();
                this.lightbox = GLightbox({
                    elements: this.buildLightboxElements(),
                    touchNavigation: true,
                    loop: false
                });
            });
        },
        destroyLightbox() {
            if (this.lightbox) {
                this.lightbox.destroy();
                this.lightbox = null;
            }
        },
        openLightbox() {
            if (this.lightbox) {
                this.lightbox.openAt(0);
            }
        },
        bindParentHeight() {
            this.$nextTick(() => {
                const root_el = this.$el;
                const parent_el = root_el?.parentElement;
                if (!parent_el) {
                    return;
                }

                const update_height = () => {
                    const next_height = parent_el.clientHeight;
                    if (this.parent_height !== next_height) {
                        this.parent_height = next_height > 0 ? next_height : null;
                    }
                };

                update_height();
                this.resize_observer = new ResizeObserver(() => {
                    update_height();
                });
                this.resize_observer.observe(parent_el);
            });
        },
        unbindParentHeight() {
            if (this.resize_observer) {
                this.resize_observer.disconnect();
                this.resize_observer = null;
            }
        }
    }
};
</script>

<style scoped>
.preview-root,
.preview-media {
    min-height: 0;
}
.img-receipt {
    object-fit: contain;
    background: #ffffff;
    cursor: zoom-in;
}
.pdf-receipt {
    display: block;
    width: 100%;
    height: 100%;
    background: #ffffff;
}
</style>
